import {
  ESubCategoryStatus,
  IFrontSubCategory,
  ISubCategoryCharacteristic
} from 'models/product'
import React, {
  memo,
  useState,
  ChangeEvent,
  KeyboardEvent,
  useRef,
  useEffect
} from 'react'
import { Typography, Input, Tag, Button, Space, Divider, Checkbox } from 'antd'
import { productApi } from 'api/product'
import {
  PlusOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  EnterOutlined,
  EditOutlined
} from '@ant-design/icons'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './SubCategoryCharacteristics.module.scss'
import type { InputRef } from 'antd'

interface ISubCategoryCharacteristicsProps {
  subCategory: IFrontSubCategory
  onUpdate: (subCategory: IFrontSubCategory) => void
}

const { Title } = Typography
const { TextArea } = Input

export const SubCategoryCharacteristics: React.FC<ISubCategoryCharacteristicsProps> =
  memo(({ subCategory, onUpdate }) => {
    // Состояние для списка характеристик
    const [characteristics, setCharacteristics] = useState<
      ISubCategoryCharacteristic[]
    >([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(false)

    // Состояния для полей ввода новой характеристики
    const [newCharacteristicName, setNewCharacteristicName] = useState('')
    const [newCharacteristicValues, setNewCharacteristicValues] = useState('')
    const [newCharacteristicIsAnyValue, setNewCharacteristicIsAnyValue] =
      useState(false)
    const [newCharacteristicRequired, setNewCharacteristicRequired] =
      useState(true)

    // Состояние для активных инпутов добавления новых тегов
    // { characteristicName: true/false }
    const [addingTag, setAddingTag] = useState<{ [key: string]: boolean }>({})
    const [newTagValue, setNewTagValue] = useState<string>('')
    const newTagInputRef = useRef<InputRef>(null)

    // Состояние для редактирования названия характеристики
    const [editingCharName, setEditingCharName] = useState<{
      [key: string]: boolean
    }>({})
    const [editingCharNameValue, setEditingCharNameValue] = useState<string>('')
    const editCharNameInputRef = useRef<InputRef>(null)

    useEffect(() => {
      if (Object.values(addingTag).some((isAdding) => isAdding)) {
        newTagInputRef.current?.focus()
      }

      // Фокус на инпуте редактирования названия характеристики
      if (Object.values(editingCharName).some((isEditing) => isEditing)) {
        editCharNameInputRef.current?.focus()
      }
    }, [addingTag, editingCharName])

    // Загрузка характеристик при изменении подкатегории
    useEffect(() => {
      if (
        subCategory &&
        !subCategory.isNew &&
        subCategory.status !== ESubCategoryStatus.MODERATION
      ) {
        loadCharacteristics()
      } else {
        setCharacteristics([])
      }
    }, [subCategory])

    const loadCharacteristics = async () => {
      try {
        setIsLoadingData(true)
        const response = await productApi.getSubCategoryCharacteristics(
          subCategory._id
        )
        setCharacteristics(response.data.characteristics || [])
      } catch {
        // Если характеристики не найдены, это нормально - просто пустой массив
        setCharacteristics([])
      } finally {
        setIsLoadingData(false)
      }
    }

    const handleRemoveValueFromCharacteristic = (
      characteristicName: string,
      valueToRemove: string
    ) => {
      setCharacteristics((prev) => {
        const updatedCharacteristics = prev.map((char) => {
          if (char.name === characteristicName) {
            const newHints = char.hints.filter((hint) => hint !== valueToRemove)
            // Если после удаления тега не осталось других значений, то удаляем всю характеристику
            if (newHints.length === 0 && !char.isAnyValue) {
              return null // Помечаем для удаления
            }
            return { ...char, hints: newHints }
          }
          return char
        })
        // Фильтруем характеристики, помеченные для удаления (null)
        return updatedCharacteristics.filter(
          (char) => char !== null
        ) as ISubCategoryCharacteristic[]
      })
    }

    const handleAddCharacteristicBlock = () => {
      const trimmedName = newCharacteristicName.trim()
      const trimmedValuesStr = newCharacteristicValues.trim()

      // 1. Валидация
      if (!trimmedName) {
        showMessage('error', 'Заполните название характеристики', '')
        return
      }

      // Если не свободное заполнение, проверяем значения
      if (!newCharacteristicIsAnyValue && !trimmedValuesStr) {
        showMessage(
          'error',
          'Заполните значения характеристики или выберите "Свободное заполнение"',
          ''
        )
        return
      }

      // Проверка на уникальность названия характеристики
      const isNameExists = characteristics.some(
        (char) => char.name.toLowerCase() === trimmedName.toLowerCase()
      )

      if (isNameExists) {
        showMessage(
          'error',
          'Характеристика с таким названием уже существует',
          ''
        )
        return
      }

      // 2. Генерация блока
      let parsedValues: string[] = []

      if (!newCharacteristicIsAnyValue && trimmedValuesStr) {
        parsedValues = trimmedValuesStr
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '') // Убираем пустые значения после split

        if (parsedValues.length === 0) {
          showMessage(
            'error',
            'Введите хотя бы одно валидное значение характеристики.',
            ''
          )
          return
        }
      }

      const newCharacteristic: ISubCategoryCharacteristic = {
        name: trimmedName,
        isAnyValue: newCharacteristicIsAnyValue,
        required: newCharacteristicRequired,
        hints: parsedValues
      }

      setCharacteristics((prev) => [...prev, newCharacteristic])

      // Очистка полей ввода
      setNewCharacteristicName('')
      setNewCharacteristicValues('')
      setNewCharacteristicIsAnyValue(false)
      setNewCharacteristicRequired(true)
    }

    // Добавление нового тега в существующую характеристику
    const handleAddValueToCharacteristicPrompt = (
      characteristicName: string
    ) => {
      setAddingTag((prev) => ({
        ...prev,
        [characteristicName]: true
      }))
      setNewTagValue('') // Сбрасываем текст при начале добавления
    }

    const handleNewTagInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      setNewTagValue(e.target.value)
    }

    const handleNewTagInputConfirm = (characteristicName: string) => {
      if (!characteristicName) return

      const trimmedNewTagValue = newTagValue.trim()

      if (!trimmedNewTagValue) {
        // Если текст пустой, просто отменяем режим добавления без сообщения
        setAddingTag((prev) => ({
          ...prev,
          [characteristicName]: false
        }))
        setNewTagValue('')
        return
      }

      setCharacteristics((prev) =>
        prev.map((char) => {
          if (char.name === characteristicName) {
            if (char.hints.includes(trimmedNewTagValue)) {
              showMessage(
                'error',
                'Такое значение уже существует для данной характеристики.',
                ''
              )
              // Оставляем инпут для исправления или отмены пользователем (через Blur без текста)
              return char
            }
            return { ...char, hints: [...char.hints, trimmedNewTagValue] }
          }
          return char
        })
      )

      // Сбрасываем состояние добавления только если не было ошибки дубликата
      // (ошибка дубликата выше делает return char, не доходя сюда для этого char)
      // Это условие нужно, чтобы setNewTagValue не сработал преждевременно, если была ошибка.
      const charWithError = characteristics.find(
        (c) =>
          c.name === characteristicName && c.hints.includes(trimmedNewTagValue)
      )
      if (!charWithError || !charWithError.hints.includes(trimmedNewTagValue)) {
        // убедимся что ошибки дубликата для ТЕКУЩЕГО char не было
        setAddingTag((prev) => ({
          ...prev,
          [characteristicName]: false
        }))
        setNewTagValue('')
      }
    }

    const handleNewTagInputKeyDown = (
      e: KeyboardEvent<HTMLInputElement>,
      characteristicName: string
    ) => {
      if (e.key === 'Enter') {
        handleNewTagInputConfirm(characteristicName)
      }
    }

    // Обработчик нажатия клавиши в полях ввода новой характеристики
    const handleCharacteristicInputKeyDown = (
      e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      // Если это Enter и не зажат Shift (позволяет делать перенос строки через Shift+Enter)
      if (e.key === 'Enter' && !e.shiftKey) {
        // Для TextArea предотвращаем поведение только если не зажат Shift
        if (
          (e.target as HTMLElement).classList.contains('ant-input-textarea')
        ) {
          e.preventDefault()
        }
        handleAddCharacteristicBlock()
      }
    }

    // Включение режима редактирования названия характеристики
    const handleStartEditCharName = (
      characteristicName: string,
      currentName: string
    ) => {
      setEditingCharName((prev) => ({
        ...prev,
        [characteristicName]: true
      }))
      setEditingCharNameValue(currentName)
    }

    // Изменение значения в инпуте редактирования названия
    const handleCharNameInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      setEditingCharNameValue(e.target.value)
    }

    // Подтверждение редактирования названия
    const handleCharNameInputConfirm = (characteristicName: string) => {
      if (!characteristicName) return

      const trimmedNameValue = editingCharNameValue.trim()

      if (!trimmedNameValue) {
        showMessage('error', 'Название характеристики не может быть пустым', '')
        return
      }

      // Проверка на уникальность, исключая текущую характеристику
      const isNameExists = characteristics.some(
        (char) =>
          char.name !== characteristicName &&
          char.name.toLowerCase() === trimmedNameValue.toLowerCase()
      )

      if (isNameExists) {
        showMessage(
          'error',
          'Характеристика с таким названием уже существует',
          ''
        )
        return
      }

      setCharacteristics((prev) =>
        prev.map((char) => {
          if (char.name === characteristicName) {
            return { ...char, name: trimmedNameValue }
          }
          return char
        })
      )

      setEditingCharName((prev) => ({
        ...prev,
        [characteristicName]: false
      }))
      setEditingCharName((prev) => ({
        ...prev,
        [trimmedNameValue]: false
      }))
      setEditingCharNameValue('')
    }

    // Обработка нажатия клавиш в инпуте редактирования названия
    const handleCharNameInputKeyDown = (
      e: KeyboardEvent<HTMLInputElement>,
      characteristicName: string
    ) => {
      if (e.key === 'Enter') {
        handleCharNameInputConfirm(characteristicName)
      } else if (e.key === 'Escape') {
        setEditingCharName((prev) => ({
          ...prev,
          [characteristicName]: false
        }))
        setEditingCharNameValue('')
      }
    }

    const handleSaveCharacteristics = async () => {
      if (subCategory.isNew) {
        showMessage(
          'error',
          'Сначала заполните и сохраните название подкатегории',
          ''
        )
        return
      }
      if (subCategory.status === ESubCategoryStatus.MODERATION) {
        showMessage('error', 'Сначала подтвердите подкатегорию', '')
        return
      }
      let characteristicsToSave = characteristics
      const trimmedName = newCharacteristicName.trim()
      const trimmedValuesStr = newCharacteristicValues.trim()
      let shouldResetNewCharacteristicFields = false

      if (trimmedName) {
        if (!newCharacteristicIsAnyValue && !trimmedValuesStr) {
          showMessage(
            'error',
            'Заполните значения характеристики или выберите "Свободное заполнение"',
            ''
          )
          return
        }

        const isNameExists = characteristics.some(
          (char) => char.name.toLowerCase() === trimmedName.toLowerCase()
        )

        if (isNameExists) {
          showMessage(
            'error',
            'Характеристика с таким названием уже существует',
            ''
          )
          return
        }

        let parsedValues: string[] = []

        if (!newCharacteristicIsAnyValue && trimmedValuesStr) {
          parsedValues = trimmedValuesStr
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v !== '')

          if (parsedValues.length === 0) {
            showMessage(
              'error',
              'Введите хотя бы одно валидное значение характеристики.',
              ''
            )
            return
          }
        }

        characteristicsToSave = [
          ...characteristics,
          {
            name: trimmedName,
            isAnyValue: newCharacteristicIsAnyValue,
            required: newCharacteristicRequired,
            hints: parsedValues
          }
        ]
        shouldResetNewCharacteristicFields = true
      }

      if (!characteristicsToSave.length) {
        showMessage('error', 'Добавьте хотя бы одну характеристику', '')
        return
      }
      try {
        setIsLoading(true)
        await productApi.updateSubCategoryCharacteristics(subCategory._id, {
          characteristics: characteristicsToSave
        })
        setCharacteristics(characteristicsToSave)
        if (shouldResetNewCharacteristicFields) {
          setNewCharacteristicName('')
          setNewCharacteristicValues('')
          setNewCharacteristicIsAnyValue(false)
          setNewCharacteristicRequired(true)
        }
        showMessage('success', 'Характеристики успешно сохранены', '')
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    // Переключение режима "свободное заполнение" для существующей характеристики
    const handleToggleIsAnyValue = (characteristicName: string) => {
      setCharacteristics((prev) =>
        prev.map((char) => {
          if (char.name === characteristicName) {
            return {
              ...char,
              isAnyValue: !char.isAnyValue,
              // Если включаем свободное заполнение, очищаем hints
              hints: !char.isAnyValue ? [] : char.hints
            }
          }
          return char
        })
      )
    }

    const handleToggleRequired = (characteristicName: string) => {
      setCharacteristics((prev) =>
        prev.map((char) => {
          if (char.name === characteristicName) {
            return {
              ...char,
              required: !char.required
            }
          }
          return char
        })
      )
    }

    if (isLoadingData) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.characteristicsSection}>
            <Title level={4} className={styles.sectionTitle}>
              Характеристики:
            </Title>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Загрузка...
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.characteristicsSection}>
          <Title level={4} className={styles.sectionTitle}>
            Характеристики:
          </Title>
          {/* Динамическое отображение блоков характеристик */}
          <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            {characteristics.map((char, charIndex) => (
              // Используем React.Fragment, чтобы избежать лишней обертки для Divider
              <React.Fragment key={char.name}>
                <div className={styles.characteristicBlockContainer}>
                  {editingCharName[char.name] ? (
                    <Input
                      ref={editCharNameInputRef}
                      className={styles.inputField}
                      value={editingCharNameValue}
                      onChange={handleCharNameInputChange}
                      onBlur={() => handleCharNameInputConfirm(char.name)}
                      onKeyDown={(e) =>
                        handleCharNameInputKeyDown(e, char.name)
                      }
                      placeholder='Название характеристики'
                      suffix={
                        <EnterOutlined
                          onClick={() => handleCharNameInputConfirm(char.name)}
                          style={{ cursor: 'pointer', color: '#1677FF' }}
                        />
                      }
                    />
                  ) : (
                    <div className={styles.characteristicHeader}>
                      <Input
                        value={char.name}
                        readOnly
                        className={styles.inputField}
                        aria-label={`Название характеристики: ${char.name}`}
                        suffix={
                          <EditOutlined
                            onClick={() =>
                              handleStartEditCharName(char.name, char.name)
                            }
                            style={{
                              cursor: 'pointer',
                              color: 'rgba(0, 0, 0, 0.45)'
                            }}
                          />
                        }
                      />
                      <div className={styles.characteristicControls}>
                        <Checkbox
                          checked={char.isAnyValue}
                          onChange={() => handleToggleIsAnyValue(char.name)}
                        >
                          Свободное заполнение
                        </Checkbox>
                        <Checkbox
                          checked={char.required}
                          onChange={() => handleToggleRequired(char.name)}
                        >
                          Обязательное поле
                        </Checkbox>
                      </div>
                    </div>
                  )}

                  {/* Показываем теги только если не свободное заполнение */}
                  {!char.isAnyValue && (
                    <div className={styles.tagsContainer}>
                      {char.hints.map((hint, hintIndex) => (
                        <Tag
                          key={`${char.name}-hint-${hintIndex}`}
                          closable
                          onClose={() =>
                            handleRemoveValueFromCharacteristic(char.name, hint)
                          }
                          closeIcon={
                            <CloseOutlined className={styles.tagRemoveIcon} />
                          }
                          className={styles.tag}
                        >
                          {hint}
                        </Tag>
                      ))}
                      {/* Блок для добавления нового тега или кнопка "добавить" */}
                      {addingTag[char.name] === true ? (
                        <Input
                          ref={newTagInputRef}
                          key={`add-tag-input-${char.name}`}
                          size='small'
                          className={styles.tagEditInput}
                          value={newTagValue}
                          onChange={handleNewTagInputChange}
                          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                            handleNewTagInputKeyDown(e, char.name)
                          }
                          onBlur={() => handleNewTagInputConfirm(char.name)}
                          placeholder='Новое значение'
                          suffix={
                            <EnterOutlined
                              onClick={() =>
                                handleNewTagInputConfirm(char.name)
                              }
                              style={{ cursor: 'pointer', color: '#1677FF' }}
                            />
                          }
                        />
                      ) : (
                        <Tag
                          onClick={() =>
                            handleAddValueToCharacteristicPrompt(char.name)
                          }
                          className={`${styles.tag} ${styles.addTagButton}`}
                          icon={<PlusOutlined />}
                        >
                          добавить
                        </Tag>
                      )}
                    </div>
                  )}
                </div>
                {/* Добавляем разделитель после каждого блока */}
                <Divider className={styles.divider} />
              </React.Fragment>
            ))}

            {/* Блок для добавления новой характеристики */}
            <div className={styles.characteristicBlockContainer}>
              <div className={styles.characteristicHeader}>
                <Input
                  placeholder='Название характеристики'
                  className={styles.inputField}
                  value={newCharacteristicName}
                  onChange={(e) => setNewCharacteristicName(e.target.value)}
                  onKeyDown={handleCharacteristicInputKeyDown}
                />
                <div className={styles.characteristicControls}>
                  {/* Настройки без названия ни к чему не относятся: пустой блок
                      не сохраняется, а галочки читались как настройки блока выше */}
                  <Checkbox
                    checked={newCharacteristicIsAnyValue}
                    disabled={!newCharacteristicName.trim()}
                    onChange={(e) =>
                      setNewCharacteristicIsAnyValue(e.target.checked)
                    }
                  >
                    Свободное заполнение
                  </Checkbox>
                  <Checkbox
                    checked={newCharacteristicRequired}
                    disabled={!newCharacteristicName.trim()}
                    onChange={(e) =>
                      setNewCharacteristicRequired(e.target.checked)
                    }
                  >
                    Обязательное поле
                  </Checkbox>
                </div>
              </div>

              {/* Показываем поле ввода значений только если не свободное заполнение */}
              {!newCharacteristicIsAnyValue && (
                <TextArea
                  placeholder='Введите список характеристик через запятую для создания тегов: 49, 14, 55, 95, 33'
                  rows={3}
                  className={styles.textareaField}
                  value={newCharacteristicValues}
                  onChange={(e) => setNewCharacteristicValues(e.target.value)}
                  onKeyDown={handleCharacteristicInputKeyDown}
                />
              )}
            </div>
          </Space>

          <div className={styles.buttonsContainer}>
            <Button
              type='default'
              icon={<PlusCircleOutlined />}
              className={styles.button}
              aria-label='Добавить новую характеристику'
              onClick={handleAddCharacteristicBlock}
            />
            <Button
              type='primary'
              className={styles.button}
              onClick={handleSaveCharacteristics}
              loading={isLoading}
              disabled={isLoading || isLoadingData}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    )
  })
